<script context="module">
    let instance = 0
</script>
<script lang="ts" generics="I">
    import type { ChangeFn } from '@melt-ui/svelte/internal/helpers';

    import { slide } from 'svelte/transition';
    import debounce from 'lodash/debounce'
    import { rebounce } from '@square/svelte-store'
    import { onDestroy } from 'svelte';

    export let noResultsLabel: string
    export let label: string
    export let placeholder: string
    export let search: (value: string) => (Promise<I[]> | I[])
    $: rebouncedSearch = rebounce(search)
    export let wait = 300
    export let value: I | undefined = undefined
    export let itemToString = (item: I) => item.toString()
    export let itemToHTML = itemToString
    export let disabled = false
    export let disabledValue: string | undefined = undefined
    export let el: HTMLDivElement | undefined = undefined

    const id = `combobox-${instance++}-`

    let term = ''
    
    let loading = false
    const debouncedSearch = debounce(async () => {
        try {
            if (!term) return
            items = await rebouncedSearch(term)
        } finally {
            loading = false
        }
    }, wait) as Function & { cancel: () => any }
    onDestroy(debouncedSearch.cancel)
    
    let items: I[] | undefined, itemElements: HTMLLIElement[] = []
    const handleInputChange = (e: Event & { currentTarget: HTMLInputElement }) => {
        term = e.currentTarget.value
        if (term) {
            loading = true
            highlightedItemIndex = -1
            debouncedSearch()
        } else {
            items = []
        }
    }
    
    let inputFocused = false
    $: open = Boolean(inputFocused && term && !loading && items?.length)

    let highlightedItemIndex = -1
    function handleInputKeydown(e: KeyboardEvent & { currentTarget: HTMLInputElement }) {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (items[highlightedItemIndex+1]) highlightedItemIndex++
            else highlightedItemIndex = 0 // restart
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            if (highlightedItemIndex-1 >= 0) highlightedItemIndex--
            else highlightedItemIndex = items.length - 1
        } else if (e.key === 'Enter' && highlightedItemIndex >= 0) {
            e.preventDefault()
            value = items[highlightedItemIndex]
            e.currentTarget?.blur()
        } else if (e.key === 'Enter') {
            e.preventDefault() // normally this would cause our input to lose focus, so prevent that
        }
    }

    function handleValueChange() {
        term = itemToString(value)
    }
    $: if (value) handleValueChange()
</script>

<div class="block max-w-[556px] relative" bind:this={el}>
    <label class="text-gray-900 block mb-2 text-sm font-bold" id="{id}label" for="{id}input">{label}</label>
    <div class="relative">
        <input
            aria-autocomplete="list"
            aria-controls="{id}list"
            aria-expanded={open}
            aria-labelledby="{id}label"
            role="combobox"
            id="{id}input"
            class="rounded-md border border-gray-400 py-3 pr-14 pl-4 w-full focus:border-[#325762] text-gray-700 placeholder:text-gray-600 disabled:text-gray-600"
            on:input={handleInputChange}
            on:keydown={handleInputKeydown}
            on:cut={handleInputChange}
            on:paste={handleInputChange}
            autocomplete="off"
            value={disabled && disabledValue ? disabledValue : term}
            type="search"
            on:focus={() => inputFocused = true}
            on:blur={() => inputFocused = false}
            aria-activedescendant={highlightedItemIndex ? `${id}option-${highlightedItemIndex}` : ''}
            {disabled}
            {placeholder}
        />
        <svg class="w-6 absolute top-3 pointer-events-none right-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10 2.25C5.71979 2.25 2.25 5.71979 2.25 10C2.25 14.2802 5.71979 17.75 10 17.75C11.87 17.75 13.5853 17.0877 14.9242 15.9848L20.4697 21.5303L20.5538 21.6029C20.8474 21.8208 21.2641 21.7966 21.5303 21.5303C21.8232 21.2374 21.8232 20.7626 21.5303 20.4697L15.9848 14.9242C17.0877 13.5853 17.75 11.87 17.75 10C17.75 5.71979 14.2802 2.25 10 2.25ZM10 3.75C13.4518 3.75 16.25 6.54822 16.25 10C16.25 13.4518 13.4518 16.25 10 16.25C6.54822 16.25 3.75 13.4518 3.75 10C3.75 6.54822 6.54822 3.75 10 3.75Z" fill="#A29EA5"/>
        </svg>        
    </div>
    {#if open}
        <ul id="{id}list" role="listbox" aria-label="Results" in:slide={{ duration: 150 }} class="bg-light rounded-md absolute max-h-[300px] overflow-y-auto top-full mt-0.5 w-full z-50 shadow-[0px_4px_16px_0px_theme(colors.gray.900)1F]">
            {#if items?.length}
                {#each items as result, i}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <li
                        role="option"
                        id="{id}option-{i}"
                        class="py-3 px-4 border-t border-gray-300 first:border-none cursor-pointer aria-selected:bg-[#E7F2F4] [&_mark]:font-semibold"
                        bind:this={itemElements[i]}
                        aria-selected={highlightedItemIndex === i}
                        on:pointerover={() => highlightedItemIndex = i}
                        on:pointerdown={() => value = items[i]}
                    >
                        {@html itemToHTML(result)}
                    </li>
                {/each}
            {:else}
                <li class="py-3 px-4 text-gray-600">
                    {noResultsLabel}
                </li>
            {/if}
        </ul>
    {/if}
</div>